import { atom } from "jotai";
import { atomWithStorage, createJSONStorage } from "jotai/utils";

import type { LoginOutput } from "@/api/custom_types";

export type UserIntent = "BUY" | "SELL" | "BOTH";

export interface IntentPermissions {
	navigation: {
		dashboard: boolean;
		orders: boolean;
		senders: boolean;
		proxy: boolean;
		buyerRelations: boolean;
		sellerRelations: boolean;
		profile: boolean;
		payment: boolean;
		settings: boolean;
	};
	dashboard: {
		showSenderStats: boolean;
	};
	orders: {
		showBuyerTab: boolean;
		showSellerTab: boolean;
		defaultTab: 0 | 1; // 0 = buyer/purchases, 1 = seller/sales
	};
	payment: {
		showSentTab: boolean;
		showReceivedTab: boolean;
		defaultTab: "sent" | "received";
	};
}

export const INTENT_PERMISSIONS: Record<UserIntent, IntentPermissions> = {
	BUY: {
		navigation: {
			dashboard: true,
			orders: true,
			senders: false,
			proxy: false,
			buyerRelations: false,
			sellerRelations: true,
			profile: true,
			payment: true,
			settings: false,
		},
		dashboard: {
			showSenderStats: false,
		},
		orders: {
			showBuyerTab: true,
			showSellerTab: false,
			defaultTab: 0,
		},
		payment: {
			showSentTab: true,
			showReceivedTab: false,
			defaultTab: "sent",
		},
	},
	SELL: {
		navigation: {
			dashboard: true,
			orders: true,
			senders: true,
			proxy: true,
			buyerRelations: true,
			sellerRelations: false,
			profile: true,
			payment: true,
			settings: true,
		},
		dashboard: {
			showSenderStats: true,
		},
		orders: {
			showBuyerTab: false,
			showSellerTab: true,
			defaultTab: 1,
		},
		payment: {
			// Sellers send payments too: the platform commission is one.
			showSentTab: true,
			showReceivedTab: true,
			defaultTab: "received",
		},
	},
	BOTH: {
		navigation: {
			dashboard: true,
			orders: true,
			senders: true,
			proxy: true,
			buyerRelations: true,
			sellerRelations: true,
			profile: true,
			payment: true,
			settings: true,
		},
		dashboard: {
			showSenderStats: true,
		},
		orders: {
			showBuyerTab: true,
			showSellerTab: true,
			defaultTab: 0,
		},
		payment: {
			showSentTab: true,
			showReceivedTab: true,
			defaultTab: "received",
		},
	},
};

export const currentUserAtom = atomWithStorage<LoginOutput | null>(
	"LoggedInUser",
	null,
	createJSONStorage(() => localStorage),
	{ getOnInit: true },
);
currentUserAtom.debugLabel = "currentUserAtom";

export const userIntentAtom = atomWithStorage<UserIntent | null>(
	"UserIntent",
	null,
	createJSONStorage(() => localStorage),
	{ getOnInit: true },
);
userIntentAtom.debugLabel = "userIntentAtom";

/** Opens the mode switcher from anywhere (sidebar, settings, command palette). */
export const intentDialogAtom = atom(false);
intentDialogAtom.debugLabel = "intentDialogAtom";
